import { useEffect, useMemo, useState } from 'react';
import { getForgeableSignal } from '../api/conceptCards';

const CACHE_TTL_MS = 90_000;
const REQUEST_DEBOUNCE_MS = 180;
const signalCache = new Map();

function normalizeSurfaceContext(value) {
  const input = value || {};
  return {
    surface: (input.surface || 'none').toLowerCase(),
    deck_id: input.deck_id || input.deckId || null,
    subtopic_id: input.subtopic_id || null,
    drug_id: input.drug_id || null,
    block_id: input.block_id || null,
  };
}

function makeParams(context) {
  const params = { surface: context.surface };
  if (context.deck_id) params.deck_id = context.deck_id;
  if (context.subtopic_id) params.subtopic_id = context.subtopic_id;
  if (context.drug_id) params.drug_id = context.drug_id;
  if (context.block_id) params.block_id = context.block_id;
  return params;
}

export default function useForgeableSignal(context, refreshKey = 0) {
  const normalized = useMemo(
    () => normalizeSurfaceContext(context),
    [context],
  );
  const params = useMemo(() => makeParams(normalized), [normalized]);
  const cacheKey = useMemo(() => JSON.stringify(params), [params]);
  const [state, setState] = useState({ hasForgeable: false, count: 0, loading: false });

  useEffect(() => {
    if (normalized.surface === 'none') {
      setState({ hasForgeable: false, count: 0, loading: false });
      return undefined;
    }

    const cached = signalCache.get(cacheKey);
    if (cached && (Date.now() - cached.cachedAt) < CACHE_TTL_MS) {
      setState({ hasForgeable: cached.hasForgeable, count: cached.count, loading: false });
      return undefined;
    }

    const controller = new AbortController();
    setState((prev) => ({
      hasForgeable: prev.hasForgeable,
      count: prev.count,
      loading: true,
    }));

    const timer = window.setTimeout(async () => {
      try {
        const response = await getForgeableSignal(params, { signal: controller.signal });
        const next = {
          hasForgeable: !!response.data?.has_forgeable,
          count: Number(response.data?.count || 0),
          loading: false,
        };
        signalCache.set(cacheKey, { ...next, cachedAt: Date.now() });
        setState(next);
      } catch (error) {
        if (controller.signal.aborted) return;
        setState({ hasForgeable: false, count: 0, loading: false });
      }
    }, REQUEST_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, normalized.surface, refreshKey]);

  return state;
}
